<template>
  <div class="rb-ob-form rb-match-page">
    <div class="rb-matching-loader" role="status" aria-live="polite">
      <span class="rb-logo-motion">
        <img src="@/assets/logo/roobroo-logo.png" alt="">
      </span>
      <h2 class="rb-ob-title">{{ $t('intake.matchingTitle') }}</h2>
      <p class="rb-ob-q-hint">{{ $t('intake.matchingIntro') }}</p>
      <p v-if="errorMessage" class="rb-ob-err is-visible" role="alert">{{ errorMessage }}</p>
      <div class="rb-loader-steps">
        <span
            v-for="(key, index) in stepKeys"
            :key="key"
            :class="stepClass(index)"
        >
          {{ $t('intake.' + key) }}
        </span>
      </div>
      <button
          v-if="errorMessage && recoveryMode === 'retryable'"
          class="rb-ob-submit"
          type="button"
          :disabled="running"
          style="margin-top: 16px"
          @click="retry"
      >
        {{ $t('intake.matchRetryableAction') }}
      </button>
      <button
          v-else-if="errorMessage && recoveryMode !== 'pending_review'"
          class="rb-ob-submit rb-ob-submit-secondary"
          type="button"
          style="margin-top: 16px"
          @click="dismissError"
      >
        {{ $t(recoveryMode === 'restart_screening' ? 'intake.screeningUi.restart' : 'intake.close') }}
      </button>
      <div v-if="recoveryMode === 'pending_review'" class="rb-ob-actions">
        <button class="rb-ob-submit" type="button" :disabled="running" @click="start">{{ $t('intake.rematchReviewRefresh') }}</button>
        <router-link class="rb-ob-submit rb-ob-submit-secondary" :to="{name: 'portalHome', params: {lang: $route.params.lang || 'fa'}}">{{ $t('intake.rematchBackToCareAction') }}</router-link>
        <router-link class="rb-ob-submit rb-ob-submit-secondary" :to="{name: 'portalSupport', params: {lang: $route.params.lang || 'fa'}}">{{ $t('intake.rematchSupportAction') }}</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import {
  saveIntake,
  markIntakePending,
} from '@/intake/intakeState'
import {
  isMatchmakingV2ApiEnabled,
  createMatchRun,
  getRecommendation,
  membersToPeople,
  isSupplyGapOutcome,
  isRetryableMatchFailure,
} from '@/intake/sandboxMatchApi'
import {
  loadScreeningStore,
  getFinalizedSnapshotId,
} from '@/intake/screeningSession'
import {
  fetchActiveClinicalResponse,
  startClinicalRematch,
} from '@/intake/portalApi'
import {
  saveMatchSession,
  clearMatchSession,
  loadMatchSession,
} from '@/intake/sandboxMatchSession'
import {hasClientSession} from '@/router/authSession'
import {clientError} from '@/intake/clientMessages'

const STEP_KEYS = [
  'matchingStepPath',
  'matchingStepSkill',
  'matchingStepTimes',
]

export default {
  name: 'MatchLoadingStep',
  data() {
    return {
      stepKeys: STEP_KEYS,
      activeIndex: 0,
      timers: [],
      errorMessage: '',
      recoveryMode: '',
      running: false,
      retryStartsNewRun: false,
      recoverySnapshotId: null,
    }
  },
  created() {
    this.start()
  },
  beforeDestroy() {
    this.clearTimers()
  },
  methods: {
    clearTimers() {
      this.timers.forEach((id) => clearTimeout(id))
      this.timers = []
    },
    stepClass(index) {
      if (index < this.activeIndex) {
        return 'rb-step-done'
      }
      if (index === this.activeIndex) {
        return 'rb-step-active'
      }
      return ''
    },
    retry() {
      if (this.running) return
      if (this.recoveryMode !== 'retryable') {
        this.dismissError()
        return
      }
      if (isMatchmakingV2ApiEnabled()) {
        // Re-run match with the same finalized snapshot (do not wipe screening).
        return this.start({newAttempt: this.retryStartsNewRun})
      }
      this.$router.replace({name: 'intakeScreening'})
    },
    dismissError() {
      if (this.recoveryMode === 'restart_screening') {
        const route = {name: 'intakeScreening', params: {lang: this.$route.params.lang || 'fa'}}
        // A different tab/action may have already finalized newer answers. An
        // old failure must not discard that newer screening when dismissed.
        if (this.recoverySnapshotId && getFinalizedSnapshotId() !== this.recoverySnapshotId) {
          this.$router.replace(route)
          return
        }
        if (window.confirm(this.$t('intake.screeningUi.restartConfirm'))) this.$router.replace({...route, query: {restart: '1'}})
        return
      }
      if (this.recoveryMode === 'supply_gap') {
        this.$router.replace({name: 'intakeMatchGap'})
        return
      }
      if (this.recoveryMode === 'pending_review') {
        this.$router.replace({name: 'intakeClientHome'})
        return
      }
      this.$router.replace({name: 'intakeScreening'})
    },
    ensureLoggedIn(afterLogin) {
      if (hasClientSession(localStorage)) {
        return true
      }
      markIntakePending()
      this.$router.replace({
        name: 'login',
        params: {lang: this.$route.params.lang || 'fa'},
        query: {afterLogin: afterLogin || 'intakeMatching'},
      })
      return false
    },
    start(options = {}) {
      if (this.running) return
      this.clearTimers()
      if (!isMatchmakingV2ApiEnabled()) {
        this.errorMessage = 'Matchmaking V2 API is required for /fa/intake'
        this.recoveryMode = 'fatal'
        this.$router.replace({name: 'intakeScreening'})
        return
      }
      if (!this.ensureLoggedIn('intakeMatching')) {
        return
      }
      if (options.newAttempt === true) {
        const previous = loadMatchSession()
        const snapshotId = getFinalizedSnapshotId()
        if (previous && previous.pendingMatchRunSnapshotId === snapshotId
          && previous.pendingMatchRunStatus === 'RETRYABLE_DEPENDENCY_FAILURE' && previous.pendingMatchRunId) {
          // A returned terminal run is immutable. Only this explicit action starts another run.
          // Unknown/unfinished requests keep their existing key to recover safely.
          saveMatchSession({
            pendingMatchRunIdempotencyKey: ('run-' + snapshotId + '-' + this.newRunToken()).slice(0, 200),
            pendingMatchRunStatus: null,
            pendingMatchRunId: null,
          })
        }
      }
      return this.runFromFinalizedSnapshot()
    },
    async runFromFinalizedSnapshot() {
      this.running = true
      this.retryStartsNewRun = false
      this.errorMessage = ''
      this.recoveryMode = ''
      this.recoverySnapshotId = null
      this.activeIndex = 0
      this.timers.push(setTimeout(() => {
        this.activeIndex = 1
      }, 300))

      let snapshotId = null
      try {
        snapshotId = getFinalizedSnapshotId()
        const store = loadScreeningStore()
        if (!snapshotId) {
          this.errorMessage = 'غربالگری هنوز نهایی نشده است'
          this.recoveryMode = 'fatal'
          this.activeIndex = STEP_KEYS.length
          this.timers.push(setTimeout(() => {
            this.$router.replace({name: 'intakeScreening'})
          }, 1200))
          return
        }
        this.activeIndex = 2
        const previous = loadMatchSession()
        const retryKey = previous
          && previous.pendingMatchRunSnapshotId === snapshotId
          ? previous.pendingMatchRunIdempotencyKey
          : null
        if (!retryKey) {
          clearMatchSession()
        }
        await this.finishWithSnapshot({
          client_requirement_snapshot_id: snapshotId,
          screening_intake_id: store.intakeId || null,
        }, 'screening', retryKey)
      } catch (e) {
        if (e && (e.status === 401 || e.code === 'AUTH_REQUIRED')) {
          markIntakePending()
          this.$router.replace({
            name: 'login',
            params: {lang: this.$route.params.lang || 'fa'},
            query: {afterLogin: 'intakeMatching'},
          })
          return
        }
        this.handleMatchFailure(e, snapshotId)
      } finally {
        this.running = false
      }
    },
    handleMatchFailure(e, snapshotId = null) {
      this.retryStartsNewRun = !!(e && e.completedRetryableRun)
      const response = e && e.response && e.response.data
      const data = (response && response.data) || (e && e.data) || {}
      const code = data.code || data.reason_code || (response && response.code)
        || (e && (e.code || e.statusCode)) || String((e && e.message) || '')
      if (code === 'SAFETY_CLEARANCE_REQUIRED' || code === 'SNAPSHOT_NOT_OWNED') {
        this.retryStartsNewRun = false
        this.recoveryMode = 'pending_review'
        this.errorMessage = clientError(e,this.$route.params.lang || 'fa')
        this.activeIndex = STEP_KEYS.length
        return
      }
      if (code === 'SAFETY_AUTHORITY_UNAVAILABLE') {
        // This is a temporary verification failure, not a safety clearance.
        // Retain the same request identity and never create a replacement run.
        this.retryStartsNewRun = false
        this.recoveryMode = 'retryable'
        this.errorMessage = clientError(e, this.$route.params.lang || 'fa')
        this.activeIndex = STEP_KEYS.length
        return
      }
      if (code === 'SNAPSHOT_NOT_FOUND' || code === 'SNAPSHOT_STALE') {
        this.retryStartsNewRun = false
        this.recoveryMode = 'restart_screening'
        this.recoverySnapshotId = snapshotId
        this.errorMessage = clientError(e, this.$route.params.lang || 'fa')
        this.activeIndex = STEP_KEYS.length
        return
      }
      if (code === 'EXPLANATION_INPUT_INVALID' || code === 'REMATCH_FRESH_REQUIREMENTS_REQUIRED') {
        this.recoveryMode = 'restart_screening'
        this.errorMessage = this.$t('intake.matchUi.restartNeeded')
        return
      }
      if (code === 'NO_MEMBERS' || code === 'EXPLANATION_COPY_UNAVAILABLE') {
        saveMatchSession({pendingMatchRunSnapshotId: null, pendingMatchRunIdempotencyKey: null})
        this.recoveryMode = 'retryable'
        this.errorMessage = this.$t('intake.matchUi.refreshNeeded')
        return
      }
      const errorText = String((e && e.message) || code || '')
      if (['REMATCH_OLD_APPOINTMENTS_OPEN', 'REMATCH_OLD_HOLDS_OPEN', 'REMATCH_OLD_PAYMENT_UNRESOLVED', 'REMATCH_OLD_SESSION_STATE_UNKNOWN'].some(value => String(code).includes(value) || errorText.includes(value))) {
        this.recoveryMode = 'pending_review'
        this.errorMessage = this.$t('intake.rematchValueReviewPending')
        this.activeIndex = STEP_KEYS.length
        return
      }
      if (String(code).includes('REMATCH_REPLACEMENT_ALREADY_STARTED') || errorText.includes('REMATCH_REPLACEMENT_ALREADY_STARTED')) {
        this.recoveryMode = 'pending_review'
        this.errorMessage = this.$t('intake.rematchBookingInProgress')
        this.activeIndex = STEP_KEYS.length
        return
      }
      if (String(code).includes('REMATCH_MANUAL_REVIEW_REQUIRED') || errorText.includes('REMATCH_MANUAL_REVIEW_REQUIRED')) {
        this.recoveryMode = 'pending_review'
        this.errorMessage = this.$t('intake.rematchManualReviewPending')
        this.activeIndex = STEP_KEYS.length
        return
      }
      if (String(code).includes('REMATCH_FINANCIAL_RESOLUTION_REQUIRED') || errorText.includes('REMATCH_FINANCIAL_RESOLUTION_REQUIRED')) {
        this.recoveryMode = 'pending_review'
        this.errorMessage = this.$t('intake.rematchValueReviewPending')
        this.activeIndex = STEP_KEYS.length
        return
      }
      if (String(code).includes('REMATCH_SAFETY_APPROVAL_REQUIRED') || errorText.includes('REMATCH_SAFETY_APPROVAL_REQUIRED')) {
        this.recoveryMode = 'pending_review'
        this.errorMessage = this.$t('intake.rematchSafetyReviewPending')
        this.activeIndex = STEP_KEYS.length
        return
      }
      if (String(code).includes('REMATCH_SAFETY_BLOCKED') || errorText.includes('REMATCH_SAFETY_BLOCKED')) {
        this.recoveryMode = 'pending_review'
        this.errorMessage = this.$t('intake.rematchSafetyBlocked')
        this.activeIndex = STEP_KEYS.length
        return
      }
      if (String(code).includes('REMATCH_STATUS_NOT_READY') || errorText.includes('REMATCH_STATUS_NOT_READY')) {
        this.recoveryMode = 'pending_review'
        this.errorMessage = this.$t('intake.rematchStatusUnavailable')
        this.activeIndex = STEP_KEYS.length
        return
      }
      if (isSupplyGapOutcome(code) || isSupplyGapOutcome(e && e.message)) {
        this.recoveryMode = 'supply_gap'
        this.errorMessage = this.$t('intake.matchGapBody')
        this.activeIndex = STEP_KEYS.length
        this.$router.replace({name: 'intakeMatchGap'})
        return
      }
      if (isRetryableMatchFailure(code) || isRetryableMatchFailure(e && e.message)) {
        this.recoveryMode = 'retryable'
        this.errorMessage = this.$t('intake.matchRetryableBody')
        this.activeIndex = STEP_KEYS.length
        return
      }
      this.recoveryMode = 'retryable'
      this.errorMessage = clientError(e, this.$route.params.lang || 'fa')
      this.activeIndex = STEP_KEYS.length
    },
    async finishWithSnapshot(snap, source, retryKey = null) {
      const snapshotId = snap.client_requirement_snapshot_id
      if (!snapshotId) {
        throw new Error('SNAPSHOT_ID_MISSING')
      }
      const previous = loadMatchSession()
      const matchRunIdempotencyKey = retryKey
        || (previous
          && previous.pendingMatchRunSnapshotId === snapshotId
          && previous.pendingMatchRunIdempotencyKey)
        || ('run-' + snapshotId + '-' + this.newRunToken()).slice(0, 200)
      saveMatchSession({
        pendingMatchRunSnapshotId: snapshotId,
        pendingMatchRunIdempotencyKey: matchRunIdempotencyKey,
        pendingMatchRunStatus: null,
        pendingMatchRunId: null,
      })
      // An unavailable clinical status must not fall through into ordinary matching.
      const clinical = await fetchActiveClinicalResponse()
      const activeRematch = clinical && clinical.rematch
      if (activeRematch && ['MANUAL_REVIEW_REQUIRED', 'FINANCIAL_RESOLUTION_REQUIRED', 'SAFETY_APPROVAL_REQUIRED', 'SAFETY_BLOCKED'].includes(activeRematch.status)) {
        const codes = {
          MANUAL_REVIEW_REQUIRED: 'REMATCH_MANUAL_REVIEW_REQUIRED',
          FINANCIAL_RESOLUTION_REQUIRED: 'REMATCH_FINANCIAL_RESOLUTION_REQUIRED',
          SAFETY_APPROVAL_REQUIRED: 'REMATCH_SAFETY_APPROVAL_REQUIRED',
          SAFETY_BLOCKED: 'REMATCH_SAFETY_BLOCKED',
        }
        const error = new Error(codes[activeRematch.status])
        error.code = codes[activeRematch.status]
        throw error
      }
      const useClinicalRematch = activeRematch
        && (['ACCEPTED_PENDING_REQUIREMENTS','MATCH_FAILED','NO_SUPPLY'].includes(activeRematch.status) || activeRematch.status === 'MATCH_READY')
      if (activeRematch && !useClinicalRematch) {
        const error = new Error('REMATCH_STATUS_NOT_READY')
        error.code = 'REMATCH_STATUS_NOT_READY'
        throw error
      }
      const run = useClinicalRematch
        ? await startClinicalRematch(activeRematch.id, snapshotId, matchRunIdempotencyKey)
        : await createMatchRun(snapshotId, {idempotencyKey: matchRunIdempotencyKey})
      if (!run.recommendation_id) {
        const status = run.status || 'SUPPLY_GAP'
        const err = new Error(status)
        err.code = status
        const pending = loadMatchSession()
        if (isRetryableMatchFailure(status) && typeof run.run_id === 'string' && run.run_id.trim()
          && pending && pending.pendingMatchRunSnapshotId === snapshotId
          && pending.pendingMatchRunIdempotencyKey === matchRunIdempotencyKey) {
          saveMatchSession({pendingMatchRunStatus: status, pendingMatchRunId: run.run_id})
          err.completedRetryableRun = true
        }
        throw err
      }
      this.activeIndex = 3
      const rec = await getRecommendation(run.recommendation_id)
      const displayTimezone = (rec.explanation && rec.explanation.display_timezone) || 'UTC'
      const people = membersToPeople(rec.members, this.$route.params.lang || 'fa', displayTimezone)
      if (!people.length) {
        const err = new Error('NO_MEMBERS')
        err.code = 'NO_MEMBERS'
        throw err
      }
      const explanation = rec.explanation && typeof rec.explanation.client_reflection_rendered_fa === 'string'
        ? rec.explanation.client_reflection_rendered_fa : ''
      saveMatchSession({
        snapshotId,
        screeningIntakeId: snap.screening_intake_id || null,
        runId: run.run_id,
        recommendationId: run.recommendation_id,
        expiresAt: run.expires_at || rec.expires_at,
        people,
        explanation,
        topicChips: (rec.explanation && rec.explanation.selected_need_display_items) || [],
        displayTimezone,
        source: useClinicalRematch ? 'clinical-rematch' : source,
        pendingMatchRunSnapshotId: null,
        pendingMatchRunIdempotencyKey: null,
        pendingMatchRunStatus: null,
        pendingMatchRunId: null,
      })
      saveIntake({
        therapistId: '',
        bookingDate: '',
        bookingTime: '',
      })
      this.$router.replace({name: 'intakeMatches'})
    },
    newRunToken() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID()
      }
      return String(Date.now()) + '-' + Math.random().toString(16).slice(2)
    },
  },
}
</script>
